'use client'

import { create } from 'zustand'
import { authService, AuthSession, AuthCredentials, AppUser } from '../../services/auth/auth-service'
import { handleAuthError } from '@/lib/api-error-handler'

// Backwards-compat alias
export type RedmineUser = AppUser

interface AuthStore {
  user: AppUser | null
  session: AuthSession | null
  isLoading: boolean
  isInitialized: boolean
  error: string | null
  login: (credentials: AuthCredentials) => Promise<void>
  loginWithApiKey: (apiKey: string) => Promise<void>
  loginFromToken: (accessToken: string, refreshToken: string, expiresIn: number, rawUser: { id: string; email: string; user_metadata?: Record<string, any> }) => void
  logout: () => void
  initialize: () => Promise<void>
  refreshSession: () => Promise<void>
  extendSession: () => void
  isAuthenticated: () => boolean
  clearError: () => void
  initializeAuth: () => Promise<void>
  hydrateProfile: () => Promise<void>
  register: (email: string, password: string, profile: { firstname: string; lastname: string }) => Promise<void>
  verifySignUp: (email: string, code: string) => Promise<void>
}

export const useAuthStore = create<AuthStore>()((set, get) => ({
  user: null,
  session: null,
  isLoading: false,
  isInitialized: false,
  error: null,

  login: async (credentials: AuthCredentials) => {
    set({ isLoading: true, error: null })
    try {
      const session = await authService.login(credentials)
      set({ session, user: session.user, isLoading: false, error: null })
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Login failed'
      set({ isLoading: false, error: errorMessage, session: null, user: null })
      throw error
    }
  },

  loginFromToken: (accessToken, refreshToken, expiresIn, rawUser) => {
    const session = authService.initFromRawToken(accessToken, refreshToken, expiresIn, rawUser)
    set({ session, user: session.user, isLoading: false, error: null, isInitialized: true })
  },

  // OrbitNest uses email/password — API key login is not supported
  loginWithApiKey: async (_apiKey: string) => {
    set({ isLoading: false, error: 'API key login is not supported. Please use email and password.' })
    throw new Error('API key login is not supported')
  },

  logout: () => {
    authService.logout()
    set({ session: null, user: null, error: null, isLoading: false })
  },

  refreshSession: async () => {
    try {
      const session = await authService.refreshSession()
      if (session) {
        set({ user: session.user, session })
      }
    } catch {
      set({ user: null, session: null, isLoading: false })
    }
  },

  extendSession: () => {
    const session = authService.getSession()
    if (session) set({ session })
  },

  clearError: () => set({ error: null }),

  initializeAuth: async () => {
    const currentState = get()

    if (currentState.isInitialized) return

    if (currentState.session?.user) {
      if (currentState.session.expiresAt > Date.now()) {
        set({ session: currentState.session, user: currentState.session.user, isLoading: false, isInitialized: true, error: null })
        return
      } else {
        set({ session: null, user: null, isInitialized: true, isLoading: false })
        handleAuthError({ message: 'Session expired' })
        return
      }
    }

    set({ isLoading: true })

    try {
      const session = authService.getSession()
      if (session?.user && session.expiresAt > Date.now()) {
        set({ session, user: session.user, isLoading: false, isInitialized: true, error: null })
      } else if (session && session.expiresAt <= Date.now()) {
        set({ isLoading: false, isInitialized: true, session: null, user: null })
        handleAuthError({ message: 'Session expired' })
      } else {
        set({ isLoading: false, isInitialized: true, session: null, user: null })
      }
    } catch {
      set({ isLoading: false, isInitialized: true, session: null, user: null })
    }
  },

  initialize: async function () {
    return get().initializeAuth()
  },

  // Refresh the in-memory user from the canonical org profile (user_profiles).
  // The session restored from storage / a fresh token may carry stale or
  // missing name fields; this keeps the header and menus showing the real name.
  hydrateProfile: async () => {
    const state = get()
    const token = state.session?.token
    if (!token || !state.user) return
    try {
      const res = await fetch('/api/users/current', {
        headers: { Authorization: `Bearer ${token}` },
        cache: 'no-store',
      })
      if (!res.ok) return
      const data = await res.json()
      const p = data?.user
      if (!p) return
      set((s) => {
        if (!s.user) return s
        const merged = {
          ...s.user,
          firstname: p.firstname || s.user.firstname,
          lastname: p.lastname || s.user.lastname,
          login: p.login || s.user.login,
          email: p.email || s.user.email,
          mail: p.email || s.user.mail,
          avatar_url: p.avatar_url || s.user.avatar_url,
          is_admin: p.is_admin ?? s.user.is_admin,
          admin: p.is_admin ?? s.user.admin,
        }
        return { user: merged, session: s.session ? { ...s.session, user: merged } : s.session }
      })
    } catch {
      // Best-effort — the stored user remains in place on failure.
    }
  },

  register: async (email, password, profile) => {
    set({ isLoading: true, error: null })
    try {
      await authService.register(email, password, profile)
      set({ isLoading: false })
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Registration failed'
      set({ isLoading: false, error: msg })
      throw error
    }
  },

  verifySignUp: async (email, code) => {
    set({ isLoading: true, error: null })
    try {
      const session = await authService.verifySignUp(email, code)
      set({ session, user: session.user, isLoading: false, error: null, isInitialized: true })
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Verification failed'
      set({ isLoading: false, error: msg })
      throw error
    }
  },

  isAuthenticated: () => {
    const state = get()
    return !!state.session && !!state.user
  },
}))

export const useAuth = () => {
  const store = useAuthStore()
  return {
    user: store.user,
    session: store.session,
    isAuthenticated: !!store.session && !!store.user,
    isLoading: store.isLoading,
    isInitialized: store.isInitialized,
    error: store.error,
    login: store.login,
    loginWithApiKey: store.loginWithApiKey,
    loginFromToken: store.loginFromToken,
    logout: store.logout,
    refreshSession: store.refreshSession,
    extendSession: store.extendSession,
    clearError: store.clearError,
    initializeAuth: store.initializeAuth,
    hydrateProfile: store.hydrateProfile,
    register: store.register,
    verifySignUp: store.verifySignUp,
  }
}

export const useUser = () => useAuthStore((state) => state.user)
export const useIsAuthenticated = () => {
  const session = useAuthStore((state) => state.session)
  const user = useAuthStore((state) => state.user)
  return !!session && !!user
}
